import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ShieldAlert, FileText, Calendar, MessageCircle, Users, RotateCcw, Archive, Loader2, Printer } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDateFullTime } from "../lib/dateFormat";

/**
 * Feb 2026 · DSGVO Art. 17 · Deletion-Preview-Modal
 * (5 Sep 2026 · User-Directive „Preview vor PIN, damit versehentliche
 * Loeschungen fast unmoeglich sind").
 *
 * Zeigt vor dem PIN-Prompt eine transparente Aufstellung darueber, was
 * beibehalten (Rechnungen · Buchungen · Anfragen · Berichte) und was
 * entfernt/anonymisiert wird (Nachrichten · Push-Tokens · Consent-Log ·
 * Read-Markierungen · Family-Accounts). Fuer Concierges zeigt das Modal
 * zusaetzlich den Assignment-Sweep-Count.
 */
export default function DeletionPreviewModal({ open, row, onCancel, onContinue }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user: currentAdmin } = useAuth();
  const nowStr = deDateFullTime(new Date());
  const rowId = row?.id;
  // Callback-Prop per Ref lesen — Inline-Arrows im Parent sollen den Fetch nicht neu triggern.
  const onCancelRef = useRef(onCancel);
  useEffect(() => { onCancelRef.current = onCancel; });

  useEffect(() => {
    if (!open || !rowId) { setPreview(null); return; }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/allowed-emails/${rowId}/deletion-preview`);
        setPreview(data);
      } catch (e) {
        toast.error(formatErr(e.response?.data?.detail) || e.message);
        onCancelRef.current?.();
      } finally {
        setLoading(false);
      }
    })();
  }, [open, rowId]);

  if (!open || !row) return null;

  const roleLabel = { customer: "Kunde", guest: "Gast", concierge: "Concierge", partner: "Partner" }[row.role] || row.role;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 deletion-preview-overlay"
      style={{ background: "rgba(13,25,48,0.6)" }}
      data-testid="deletion-preview-modal"
    >
      {/* Print-CSS: nur der Modal-Inhalt wird gedruckt, alles andere weg */}
      <style>{`
        @media print {
          @page { size: A4; margin: 18mm 16mm; }
          body * { visibility: hidden !important; }
          .deletion-preview-overlay,
          .deletion-preview-overlay * { visibility: visible !important; }
          .deletion-preview-overlay {
            position: static !important;
            background: white !important;
            padding: 0 !important;
            display: block !important;
          }
          .deletion-preview-sheet {
            max-height: none !important;
            overflow: visible !important;
            border: none !important;
            box-shadow: none !important;
            padding: 0 !important;
            background: white !important;
          }
          .print-only { display: block !important; }
          .no-print { display: none !important; }
        }
        .print-only { display: none; }
      `}</style>
      <div
        className="bg-ivory max-w-2xl w-full max-h-[92vh] overflow-y-auto p-8 border deletion-preview-sheet"
        style={{ borderColor: withAlpha(BRAND_COLORS.gold, 0.3) }}
      >
        {/* Print-only header — nur beim Ausdruck sichtbar */}
        <div
          className="print-only mb-6 pb-4 border-b"
          style={{ borderColor: BRAND_COLORS.navy }}
          data-testid="deletion-preview-print-header"
        >
          <div style={{ fontSize: "10px", letterSpacing: "3px", textTransform: "uppercase", color: "#c9a961" }}>
            Weissblom
          </div>
          <div style={{ fontSize: "18px", fontFamily: "Georgia,serif", color: "#0d1930", marginTop: "6px" }}>
            Löschungs-Vorschau · DSGVO Art. 17 / §147 AO
          </div>
          <div style={{ fontSize: "11px", color: "#475569", marginTop: "8px" }}>
            Ausgedruckt am: {nowStr}
            {currentAdmin?.name || currentAdmin?.email
              ? ` · durch ${currentAdmin.name || currentAdmin.email}`
              : ""}
          </div>
        </div>

        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="label-caps text-red-800 flex items-center gap-2 mb-2 no-print">
              <ShieldAlert className="w-3.5 h-3.5" strokeWidth={1.6} />
              DSGVO Art. 17 · §147 AO
            </div>
            <h2 className="font-display text-2xl md:text-3xl text-navy leading-tight">
              {roleLabel} löschen?
            </h2>
            <p className="text-[13px] text-navy-dark/60 mt-1">{row.email}</p>
          </div>
          <button
            onClick={onCancel}
            data-testid="deletion-preview-close"
            className="text-navy-dark/40 hover:text-navy no-print"
          >
            <X className="w-5 h-5" strokeWidth={1.6} />
          </button>
        </div>

        {loading && (
          <div className="py-10 text-center label-caps text-navy-dark/60" data-testid="deletion-preview-loading">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" strokeWidth={1.8} />
            Vorschau wird berechnet …
          </div>
        )}

        {!loading && preview && !preview.user_exists && (
          <div
            className="mt-6 p-4 border"
            style={{ borderColor: withAlpha(BRAND_COLORS.gold, 0.5), background: withAlpha(BRAND_COLORS.gold, 0.06) }}
            data-testid="deletion-preview-never-logged-in"
          >
            <div className="label-caps text-goldDark mb-1.5">Sofortige Entfernung</div>
            <p className="text-[13px] text-navy-dark/80 leading-relaxed">
              {preview.note}
            </p>
          </div>
        )}

        {!loading && preview && preview.user_exists && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {/* Retention */}
              <div className="border p-4" style={{ borderColor: withAlpha(BRAND_COLORS.navy, 0.15), background: "white" }}>
                <div className="flex items-center gap-2 mb-3">
                  <Archive className="w-4 h-4" strokeWidth={1.8} style={{ color: BRAND_COLORS.goldDark }} />
                  <span className="label-caps text-goldDark">Bleibt erhalten</span>
                </div>
                <ul className="space-y-2 text-[13px]">
                  <PreviewRow icon={FileText} label="Rechnungen (§147 AO · 10 J.)" value={preview.kept.invoices} testId="prev-invoices" />
                  {(preview.role === "customer" || preview.role === "guest") && (
                    <>
                      <PreviewRow icon={Calendar} label="Buchungen" value={preview.kept.bookings} testId="prev-bookings" />
                      <PreviewRow icon={FileText} label="Anfragen" value={preview.kept.requests} testId="prev-requests" />
                      <PreviewRow icon={FileText} label="Wochenberichte" value={preview.kept.weekly_updates} testId="prev-updates" />
                    </>
                  )}
                </ul>
              </div>

              {/* Removed */}
              <div className="border p-4" style={{ borderColor: "rgba(139,30,30,0.3)", background: "white" }}>
                <div className="flex items-center gap-2 mb-3">
                  <ShieldAlert className="w-4 h-4" strokeWidth={1.8} style={{ color: "#8B1E1E" }} />
                  <span className="label-caps" style={{ color: "#8B1E1E" }}>Wird entfernt / anonymisiert</span>
                </div>
                <ul className="space-y-2 text-[13px]">
                  <PreviewRow icon={MessageCircle} label="Nachrichten" value={preview.removed.messages} testId="prev-messages" />
                  <PreviewRow icon={FileText} label="Consent-Log" value={preview.removed.consent_log} testId="prev-consent" />
                  <PreviewRow icon={FileText} label="Push-Tokens" value={preview.removed.push_tokens} testId="prev-push" />
                  <PreviewRow icon={FileText} label="Lesemarken" value={preview.removed.weekly_update_reads} testId="prev-reads" />
                  {(preview.role === "customer" || preview.role === "guest") && preview.removed.family_accounts > 0 && (
                    <PreviewRow icon={Users} label="Family-Sub-Accounts" value={preview.removed.family_accounts} testId="prev-family" />
                  )}
                </ul>
                <p className="text-[11px] text-navy-dark/60 mt-3 leading-snug">
                  Name, E-Mail, Telefon werden auf „[gelöscht]" gesetzt · E-Mail-Hash wird in die Sperrliste eingetragen (verhindert Wiederregistrierung).
                </p>
              </div>
            </div>

            {preview.role === "concierge" && preview.assignments_reset && (
              <div
                className="mt-4 p-4 border"
                style={{ borderColor: withAlpha(BRAND_COLORS.gold, 0.5), background: withAlpha(BRAND_COLORS.gold, 0.06) }}
                data-testid="deletion-preview-concierge-sweep"
              >
                <div className="flex items-center gap-2 mb-2">
                  <RotateCcw className="w-4 h-4" strokeWidth={1.8} style={{ color: BRAND_COLORS.goldDark }} />
                  <span className="label-caps text-goldDark">Concierge-Sweep · sofortige Freigabe</span>
                </div>
                <ul className="text-[13px] text-navy-dark/80 space-y-1">
                  <li data-testid="prev-sweep-requests">
                    <b>{preview.assignments_reset.requests}</b> offene Anfragen werden auf „nicht zugeteilt" zurückgesetzt.
                  </li>
                  <li data-testid="prev-sweep-bookings">
                    <b>{preview.assignments_reset.bookings}</b> offene Buchungen werden freigegeben.
                  </li>
                  <li data-testid="prev-sweep-tasks">
                    <b>{preview.assignments_reset.tasks}</b> offene Aufgaben werden freigegeben.
                  </li>
                  <li className="text-[11px] text-navy-dark/60 mt-2">
                    Erledigte Zuweisungen bleiben unverändert. Reaktivierung stellt Zuweisungen NICHT wieder her.
                  </li>
                </ul>
              </div>
            )}

            <div
              className="mt-4 p-4 border-l-4 text-[12px] leading-relaxed"
              style={{ borderColor: "#8B1E1E", background: "rgba(139,30,30,0.05)", color: "#621818" }}
              data-testid="deletion-preview-warning"
            >
              <b>30-Tage-Karenz:</b> Der Account wird sofort deaktiviert (kein Login mehr möglich).
              Nach 30 Tagen wird die Anonymisierung automatisch ausgeführt. Bis dahin ist eine
              Reaktivierung mit Admin-PIN möglich. Der Nutzer erhält eine Courtesy-Mail mit
              Self-Service-Reaktivier-Link.
            </div>
          </>
        )}

        <div className="mt-6 flex items-center justify-end gap-3 no-print">
          <button
            type="button"
            data-testid="deletion-preview-print"
            disabled={loading || !preview}
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 text-navy-dark px-4 py-2 text-[11px] tracking-widest uppercase border disabled:opacity-40 hover:bg-navy/5"
            style={{ borderColor: withAlpha(BRAND_COLORS.navy, 0.3) }}
          >
            <Printer className="w-3.5 h-3.5" strokeWidth={1.8} />
            Vorschau drucken
          </button>
          <div className="flex-1" />
          <button
            type="button"
            onClick={onCancel}
            data-testid="deletion-preview-cancel"
            className="text-[11px] tracking-widest uppercase text-navy-dark/60 hover:text-navy px-4 py-2"
          >
            Abbrechen
          </button>
          <button
            type="button"
            data-testid="deletion-preview-continue"
            disabled={loading}
            onClick={() => onContinue(preview)}
            className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-widest uppercase disabled:opacity-40"
            style={{ background: "#8B1E1E" }}
          >
            <ShieldAlert className="w-3.5 h-3.5" strokeWidth={1.8} />
            Weiter zur PIN
          </button>
        </div>

        {/* Print-only Fussbereich mit Unterschriftszeilen */}
        <div
          className="print-only mt-10 pt-6 border-t"
          style={{ borderColor: "#0d1930" }}
          data-testid="deletion-preview-print-footer"
        >
          <div style={{ fontSize: "10px", color: "#475569", lineHeight: 1.5, marginBottom: "36px" }}>
            Hinweis: Diese Vorschau dokumentiert den Umfang der Löschung nach DSGVO Art. 17
            (Recht auf Vergessenwerden) und die Retention nach §147 AO (Rechnungen 10 Jahre).
            Sie ersetzt keinen formalen Rechnungsauszug und ist als Beleg fuer die interne
            Bearbeitung sowie zur Vorlage beim Betroffenen bestimmt.
          </div>
          <div style={{ display: "flex", gap: "40px", justifyContent: "space-between" }}>
            <div style={{ flex: 1 }}>
              <div style={{ borderTop: "1px solid #0d1930", paddingTop: "6px", fontSize: "10px", letterSpacing: "2px", textTransform: "uppercase", color: "#475569" }}>
                Unterschrift Nutzer · Datum
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ borderTop: "1px solid #0d1930", paddingTop: "6px", fontSize: "10px", letterSpacing: "2px", textTransform: "uppercase", color: "#475569" }}>
                Unterschrift Admin · Datum
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function PreviewRow({ icon: Icon, label, value, testId }) {
  return (
    <li className="flex items-center justify-between" data-testid={testId}>
      <span className="flex items-center gap-2 text-navy-dark/80">
        <Icon className="w-3.5 h-3.5" strokeWidth={1.6} />
        {label}
      </span>
      <span className="tabular-nums text-navy font-medium">{value ?? 0}</span>
    </li>
  );
}
